
import { baseApi } from "../../api/baseApi";

const accountApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    applicant: builder.query({
      query: () => ({
        url: `/education/applicant/profile/`,
        method: "GET",
      }),
      providesTags: ["applicant"],
    }),
    updateApplicant: builder.mutation({
      query: (applicantData) => ({
        url: "/education/applicant/profile/",
        method: "POST",
        body: applicantData,
      }),
      invalidatesTags: ["applicant"],
    }),
    getProfile: builder.query({
      query: () => ({
        url: "/account/profile/",
        method: "GET",
      }),
      providesTags: ["profile"],
    }),
    deleteProfile: builder.mutation({
      query: (id) => ({
        url: `/account/delete/${id}/`,
        method: "DELETE",
      }),
      // AdminUserData backs the system users / students lists — without it the
      // deleted row lingers until a refetch.
      invalidatesTags: ["profile", "AdminUserData"],
    }),

    updateProfile: builder.mutation({
      query: (profileData) => ({
        url: "/account/profile/",
        method: "PUT",
        body: profileData,
      }),
      // "profile" too: useProfileQuery provides that tag, so without it the
      // header/sidebar kept showing stale data after an update.
      invalidatesTags: ["applicant", "profile"],
    }),
    uploadProfileImage: builder.mutation({
      query: (formData) => ({
        url: "/account/profile/image/",
        method: "POST",
        // No Content-Type header — the browser must set the multipart
        // boundary itself, and fetchBaseQuery leaves FormData alone.
        body: formData,
      }),
      invalidatesTags: ["profile", "applicant"],
    }),
    deleteProfileImage: builder.mutation({
      query: () => ({
        url: "/account/profile/image/",
        method: "DELETE",
      }),
      invalidatesTags: ["profile", "applicant"],
    }),
    getCountry: builder.query({
      query: () => ({
        url: "/account/country/?records=500",
        method: "GET",
      }),
      providesTags: ["country"],
    }),

    getCountryById: builder.query({
      query: (id) => ({
        url: `/account/country/${id}/`,
        method: "GET",
      }),
      providesTags: ["country"],
    }),

    createCountry: builder.mutation({
      query: (data) => ({
        url: "/account/country/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["country"],
    }),

    updateCountry: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/account/country/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["country"],
    }),

    deleteCountry: builder.mutation({
      query: (id) => ({
        url: `/account/country/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["country"],
    }),

    getCity: builder.query({
      query: (country) => ({
        url: `/account/country/${country}/`,
        method: "GET",
      }),
      providesTags: ["city"],
    }),
    getAllCity: builder.query({
      query: () => ({
        url: "/account/city/",
        method: "GET",
      }),
      providesTags: ["city"],
    }),

    uploadCertificate: builder.mutation({
      query: (certificateData) => ({
        url: "/education/applicant/certificate/",
        method: "POST",
        body: certificateData,
      }),
      invalidatesTags: ["Certificate", "applicant"],
    }),
    adminUploadCertificate: builder.mutation({
      query: (certificateData) => ({
        url: "/admin/student-cert-manage/",
        method: "POST",
        body: certificateData,
      }),
      invalidatesTags: ["Certificate", "applicant"],
    }),
    deleteCertificate: builder.mutation({
      query: (certificateId) => {
        // console.log("Deleting certificate with ID:", certificateId);
        return {
          // No trailing slash: education/urls.py registers the detail route as
          // 'applicant/certificate/<int:pk>' (slashless). Adding one 404s.
          url: `/education/applicant/certificate/${certificateId}`,
          method: "DELETE",
        };
      },
      // "applicant" too: the certificate list is served by the applicant
      // query, so invalidating only "Certificate" never refetched it.
      invalidatesTags: ["Certificate", "applicant"],
    }),
    adminDeleteCertificate: builder.mutation({
      query: (certificateId) => {
        return {
          // Slashless for the same reason — see dashboard/urls.py.
          url: `/admin/student-cert-manage/${certificateId}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["Certificate", "applicant"],
    }),

    // ── Journey Phases ────────────────────────────────────────
    getJourneyPhases: builder.query({
      query: (countryId) => ({
        url: countryId
          ? `/account/journey-phases/?country=${countryId}`
          : `/account/journey-phases/`,
        method: "GET",
      }),
      providesTags: ["journeyPhase"],
    }),
    createJourneyPhase: builder.mutation({
      query: (data) => ({
        url: "/account/journey-phases/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["journeyPhase", "country"],
    }),
    updateJourneyPhase: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/account/journey-phases/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["journeyPhase", "country"],
    }),
    deleteJourneyPhase: builder.mutation({
      query: (id) => ({
        url: `/account/journey-phases/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["journeyPhase", "country"],
    }),

    // ── Journey Sub-Phases ────────────────────────────────────
    getJourneySubPhases: builder.query({
      query: (phaseId) => ({
        url: `/account/journey-sub-phases/?phase=${phaseId}`,
        method: "GET",
      }),
      providesTags: ["journeySubPhase"],
    }),
    createJourneySubPhase: builder.mutation({
      query: (data) => ({
        url: "/account/journey-sub-phases/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["journeyPhase", "journeySubPhase", "country"],
    }),
    updateJourneySubPhase: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/account/journey-sub-phases/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["journeyPhase", "journeySubPhase", "country"],
    }),
    deleteJourneySubPhase: builder.mutation({
      query: (id) => ({
        url: `/account/journey-sub-phases/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["journeyPhase", "journeySubPhase", "country"],
    }),

    // ── Country Knowledge Base (migration corridors) ──────────
    getKnowledgeBases: builder.query({
      query: (params = {}) => {
        const qs = new URLSearchParams();
        if (params.from) qs.append("from", params.from);
        if (params.to) qs.append("to", params.to);
        if (params.search) qs.append("search", params.search);
        const suffix = qs.toString() ? `?${qs.toString()}` : "";
        return { url: `/account/knowledge-base/${suffix}`, method: "GET" };
      },
      providesTags: ["knowledgeBase"],
    }),
    getKnowledgeBaseById: builder.query({
      query: (id) => ({
        url: `/account/knowledge-base/${id}/`,
        method: "GET",
      }),
      providesTags: ["knowledgeBase"],
    }),
    // Resolves a corridor by country pair. The backend answers 204 when no
    // entry exists, so `data` is undefined rather than an error.
    lookupKnowledgeBase: builder.query({
      query: ({ from, to }) => ({
        url: `/account/knowledge-base/lookup/?from=${from}&to=${to}`,
        method: "GET",
      }),
      providesTags: ["knowledgeBase"],
    }),
    createKnowledgeBase: builder.mutation({
      query: (data) => ({
        url: "/account/knowledge-base/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["knowledgeBase"],
    }),
    updateKnowledgeBase: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/account/knowledge-base/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["knowledgeBase"],
    }),
    deleteKnowledgeBase: builder.mutation({
      query: (id) => ({
        url: `/account/knowledge-base/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["knowledgeBase"],
    }),
  }),
});

export const {
  useApplicantQuery,
  useUpdateApplicantMutation,
  useGetProfileQuery,
  useUpdateProfileMutation,
  useUploadProfileImageMutation,
  useDeleteProfileImageMutation,
  useGetCountryQuery,
  useGetCountryByIdQuery,
  useCreateCountryMutation,
  useUpdateCountryMutation,
  useDeleteCountryMutation,
  useGetCityQuery,
  useDeleteProfileMutation,
  useGetAllCityQuery,
  useUploadCertificateMutation,
  useAdminUploadCertificateMutation,
  useDeleteCertificateMutation,
  useAdminDeleteCertificateMutation,
  useGetJourneyPhasesQuery,
  useCreateJourneyPhaseMutation,
  useUpdateJourneyPhaseMutation,
  useDeleteJourneyPhaseMutation,
  useGetJourneySubPhasesQuery,
  useCreateJourneySubPhaseMutation,
  useUpdateJourneySubPhaseMutation,
  useDeleteJourneySubPhaseMutation,
  useGetKnowledgeBasesQuery,
  useGetKnowledgeBaseByIdQuery,
  useLookupKnowledgeBaseQuery,
  useCreateKnowledgeBaseMutation,
  useUpdateKnowledgeBaseMutation,
  useDeleteKnowledgeBaseMutation,
} = accountApi;
